import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, ListChecks, FileText, ShieldCheck, Sun, Download, Mail, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, money } from "@/lib/solarApi";

const STEPS = [
  { n: "01", t: "Tell us about your project", d: "Your location, customer type and backup needs — no technical knowledge required." },
  { n: "02", t: "Pick your appliances", d: "Choose from a library of common appliances with sensible wattage defaults — edit anything." },
  { n: "03", t: "Get your design", d: "Receive your energy profile free, then unlock the full professional BNM design with BOM and PDF report." },
];

const WHY = [
  { t: "Engineered estimates", d: "Transparent assumptions — sun hours, efficiencies, margins — all shown, all adjustable." },
  { t: "Complete system view", d: "Panels, batteries, inverter, cables, protection and bill of materials in one report." },
  { t: "Built for Sierra Leone", d: "Defaults tuned to West African solar conditions by BNM engineers." },
  { t: "Honest by design", d: "Preliminary recommendations, clearly labelled — final sizing is verified on site." },
];

const FAQ = [
  { q: "Is the calculator result exact?", a: "No — and we say so openly. It is a preliminary system recommendation. Final sizing depends on site conditions, appliance specifications, solar irradiation, cable distances and battery technology, and is verified through a BNM site assessment before installation." },
  { q: "Do I need to know my appliances' wattage?", a: "No. The library includes sensible defaults for common appliances (for example, a refrigerator at 150W). You can edit any value if you know the real rating." },
  { q: "What do I get for free?", a: "Your energy profile: daily and monthly consumption, peak load and a solar size range — free, instantly." },
  { q: "What does the paid Professional Solar Design include?", a: "Complete solar, battery and inverter sizing, preliminary cable and protection recommendations, a full bill of materials, a downloadable PDF report, email delivery and follow-up from the BNM team." },
  { q: "How do I pay?", a: "Payment options are shown at checkout. BNM confirms your payment and your report unlocks automatically — you will also receive it by email." },
];

export default function Solar() {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/solar/config").then((r) => setPricing(r.data.pricing)).catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="BNM Solar Energy Calculator — Size Your Solar System"
        description="Know exactly what your home or business needs before investing in solar. BNM's calculator estimates your energy demand and recommends panels, batteries, inverter, cables and protection."
      />
      <PageHero
        testid="solar-hero"
        eyebrow="BNM Digital Service · Know Your Energy. Design Your Solar. Power Your Future."
        lines={["BNM Solar", "Energy Calculator."]}
        lead="Know exactly what your home or business needs before investing in solar. Tell us the appliances and equipment you want to power — our calculator estimates your energy demand and recommends an appropriate solar system, battery capacity, inverter, cables and related components."
      >
        <Reveal delay={0.4}>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/solar/calculator"
              data-testid="solar-start-cta"
              className="inline-flex items-center gap-2 rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian"
            >
              <Calculator className="h-4 w-4" aria-hidden="true" />
              Calculate My Solar System
            </Link>
            <a
              href="#solar-pricing"
              data-testid="solar-pricing-cta"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-7 py-3.5 font-semibold text-white transition-colors hover:border-volt/60 hover:text-volt"
            >
              View Pricing
            </a>
          </div>
        </Reveal>
      </PageHero>

      <section data-testid="solar-how" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="How It Works" title="Three steps to a professional preliminary design." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-8">
                  <p className="font-mono text-xs text-emerald-600">{s.n}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="solar-why" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <SectionHead
              eyebrow="Why Use BNM"
              title="A calculator built by engineers, not marketers."
              lead="Every number in your report traces back to a stated assumption. Nothing is hidden, nothing is inflated."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY.map((w, i) => (
                <Reveal key={w.t} delay={i * 0.06}>
                  <article className="h-full rounded-lg border border-slate-200 bg-mist p-6">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                    <h3 className="mt-3 font-display text-base font-bold tracking-tight text-slate-900">{w.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.d}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="solar-pricing" data-testid="solar-pricing" className="bg-mist grid-lines-light scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="Pricing" title="Start free. Unlock the full design when you're ready." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 max-w-4xl">
            <Reveal>
              <article className="h-full rounded-lg border border-slate-200 bg-white p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">Basic Energy Estimate</p>
                <p className="mt-3 font-display text-3xl font-extrabold text-slate-900">Free</p>
                <ul className="mt-5 space-y-2.5 text-sm text-slate-600">
                  {["Appliance energy calculation", "Estimated daily consumption", "Peak load estimate", "Basic solar size range"].map((f) => (
                    <li key={f} className="flex gap-2.5"><ListChecks className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />{f}</li>
                  ))}
                </ul>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article className="h-full rounded-lg border-2 border-emerald-500 bg-white p-8 relative">
                <span className="absolute -top-3 right-6 rounded-full bg-obsidian px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-volt">Recommended</span>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">Professional BNM Solar Design</p>
                <p className="mt-3 font-display text-3xl font-extrabold text-slate-900" data-testid="solar-price-display">
                  {pricing ? money(pricing.amount, pricing.currency) : "…"}
                </p>
                <ul className="mt-5 space-y-2.5 text-sm text-slate-600">
                  {[
                    "Complete solar, battery & inverter sizing",
                    "Preliminary cable & protection recommendations",
                    "Detailed bill of materials",
                    "Downloadable PDF report",
                    "Email delivery & BNM follow-up",
                  ].map((f) => (
                    <li key={f} className="flex gap-2.5"><ListChecks className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />{f}</li>
                  ))}
                </ul>
                <Link
                  to="/solar/calculator"
                  data-testid="solar-unlock-cta"
                  className="mt-7 inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt"
                >
                  Unlock Full Solar Design
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="solar-receive" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead dark eyebrow="What You Receive" title="A report you can hand to a contractor." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Sun, t: "System sizing", d: "Solar array, battery bank and inverter with full reasoning." },
              { icon: FileText, t: "Branded PDF", d: "Professional BNM report with appliance schedule and BOM." },
              { icon: Download, t: "Lifetime access", d: "Re-download your report from your customer dashboard." },
              { icon: Mail, t: "Email delivery", d: "Your report lands in your inbox the moment it is ready." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.06}>
                <article className="h-full rounded-lg border border-white/10 bg-pine/60 p-7">
                  <f.icon className="h-6 w-6 text-volt" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight text-white">{f.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl rounded-lg border border-white/10 bg-pine/40 p-5 text-sm leading-relaxed text-slate-400">
              <span className="font-semibold text-volt">Professional disclaimer:</span> calculator results are
              preliminary estimates. Final system sizing should be verified by a qualified BNM technician or engineer
              through a site assessment before installation.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="solar-faq" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="FAQ" title="Questions about the calculator." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 max-w-3xl">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`sfaq-${i}`} className="border-slate-200">
                  <AccordionTrigger data-testid={`solar-faq-${i}`} className="text-left font-display font-semibold text-slate-900 hover:text-emerald-700">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Know Your Energy. Design Your Solar. Power Your Future."
        text="Five minutes of inputs, a professional preliminary solar design out. Free to start."
        primary={{ label: "Calculate My Solar System", to: "/solar/calculator", testid: "solar-bottom-cta" }}
        secondary={{ label: "Talk to BNM", to: "/contact", testid: "solar-contact-cta" }}
        testid="solar-page-cta"
      />
    </>
  );
}
